import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { BookOpen, FileText, Brain, Plus, ChevronRight, Loader2, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth, getAuthHeaders } from "../App";
import DashboardHeader from "../components/DashboardHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const BooksPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { token } = useAuth();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteBook, setDeleteBook] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${API}/books`, {
        headers: getAuthHeaders(token)
      });
      // Sort books by created_at descending (most recent first)
      const sortedBooks = [...response.data].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setBooks(sortedBooks);
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteBook) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/books/${deleteBook.book_id}`, {
        headers: getAuthHeaders(token)
      });
      setBooks(books.filter(b => b.book_id !== deleteBook.book_id));
      toast.success(t('success.deleted'));
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setDeleting(false);
      setDeleteBook(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      ready: "bg-green-100 text-green-700",
      analyzing: "bg-yellow-100 text-yellow-700",
      pending: "bg-slate-100 text-slate-700",
      error: "bg-red-100 text-red-700"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <DashboardHeader />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#4F46E5]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="books-page">
      <DashboardHeader />

      <main className="container-main py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="p-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                My Books
              </h1>
              <p className="text-slate-600">Manage your uploaded books and documents</p>
            </div>
          </div>
          <Button 
            onClick={() => navigate("/books/upload")}
            className="btn-primary gap-2"
            data-testid="upload-book-btn"
          >
            <Plus className="w-4 h-4" />
            Upload Book
          </Button>
        </div>

        {books.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {books.map((book) => (
              <Card 
                key={book.book_id}
                className="card-base card-interactive group"
                data-testid={`book-card-${book.book_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-indigo-50 rounded-xl flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-[#4F46E5]" />
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(book.status)}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 text-slate-400 hover:text-red-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteBook(book);
                        }}
                        data-testid={`delete-book-${book.book_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2">{book.title}</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    {book.chapters?.length || 0} chapters • {book.content_type}
                  </p>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => navigate(`/books/${book.book_id}/chapters`)}
                      disabled={book.status !== 'ready'}
                      data-testid={`view-chapters-${book.book_id}`}
                    >
                      View Chapters
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 btn-primary"
                      onClick={() => navigate(`/books/${book.book_id}/test`)}
                      disabled={book.status !== 'ready'}
                      data-testid={`create-test-${book.book_id}`}
                    >
                      Create Test
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="card-base">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 mx-auto bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                <BookOpen className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">No books yet</h3>
              <p className="text-slate-500 mb-6 max-w-md mx-auto">
                Upload a PDF, paste a URL, or enter text to start creating tests and quizzes
              </p>
              <Button 
                onClick={() => navigate("/books/upload")}
                className="btn-primary"
                data-testid="upload-first-book-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Upload Your First Book
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteBook} onOpenChange={() => setDeleteBook(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Book?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteBook?.title}" and all associated tests and quizzes. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BooksPage;
